import { useEffect, useRef, useState } from 'react';
import { Search, X, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { socialMediaApi } from '../api';

interface TwitterUser {
  id: string;
  name: string;
  username: string;
  profileImageUrl?: string;
  description?: string;
  verified: boolean;
  verifiedType: 'blue' | 'government' | 'business' | 'none';
  followersCount: number;
}

interface TwitterUserSearchProps {
  /** Vault Holder ID — required for the Twitter user-search API. */
  holderId: string | null;
  /** Account ID of the connected Twitter account to search with */
  accountId: string | undefined;
  /** Currently selected @usernames (without leading @) */
  selectedUsernames: string[];
  onChange: (usernames: string[]) => void;
  disabled?: boolean;
}

function formatFollowers(count: number): string {
  if (count >= 1_000_000) return `${(count / 1_000_000).toFixed(1).replace(/\.0$/, '')}M`;
  if (count >= 1_000) return `${(count / 1_000).toFixed(1).replace(/\.0$/, '')}K`;
  return String(count);
}

function VerifiedBadge({ type }: { type: 'blue' | 'government' | 'business' | 'none' }) {
  if (type === 'none') return null;
  const colorClass =
    type === 'blue' ? 'text-sky-400' : type === 'business' ? 'text-amber-500' : 'text-slate-400'; // government
  return (
    <svg
      viewBox="0 0 24 24"
      aria-label={`${type} verified`}
      className={cn('h-3 w-3 shrink-0 inline-block', colorClass)}
      fill="currentColor"
    >
      <path d="M22.25 12c0-1.43-.88-2.67-2.19-3.34.46-1.39.2-2.9-.81-3.91s-2.52-1.27-3.91-.81c-.66-1.31-1.91-2.19-3.34-2.19s-2.67.88-3.33 2.19c-1.4-.46-2.91-.2-3.92.81s-1.26 2.52-.8 3.91C1.63 9.33.75 10.57.75 12s.88 2.67 2.19 3.34c-.46 1.39-.2 2.9.81 3.91s2.52 1.26 3.91.81c.66 1.31 1.91 2.19 3.34 2.19s2.67-.88 3.33-2.19c1.4.46 2.91.2 3.92-.81s1.26-2.52.8-3.91c1.31-.67 2.19-1.91 2.19-3.34zm-9.74 4.15L8.23 11.8l1.06-1.06 3.2 3.2 5.02-5.04 1.06 1.06-6.08 6.19z" />
    </svg>
  );
}

const VERIFIED_ICON_PATH =
  'M22.25 12c0-1.43-.88-2.67-2.19-3.34.46-1.39.2-2.9-.81-3.91s-2.52-1.27-3.91-.81c-.66-1.31-1.91-2.19-3.34-2.19s-2.67.88-3.33 2.19c-1.4-.46-2.91-.2-3.92.81s-1.26 2.52-.8 3.91C1.63 9.33.75 10.57.75 12s.88 2.67 2.19 3.34c-.46 1.39-.2 2.9.81 3.91s2.52 1.26 3.91.81c.66 1.31 1.91 2.19 3.34 2.19s2.67-.88 3.33-2.19c1.4.46 2.91.2 3.92-.81s1.26-2.52.8-3.91c1.31-.67 2.19-1.91 2.19-3.34zm-9.74 4.15L8.23 11.8l1.06-1.06 3.2 3.2 5.02-5.04 1.06 1.06-6.08 6.19z';

/**
 * Search Twitter users by username and select them as chips.
 * Also accepts direct @username entries typed into the field.
 */
export function TwitterUserSearch({
  holderId,
  accountId,
  selectedUsernames,
  onChange,
  disabled = false,
}: TwitterUserSearchProps) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<TwitterUser[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [verifiedOnly, setVerifiedOnly] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Debounced search
  useEffect(() => {
    const trimmed = query.replace(/^@/, '').trim();

    if (!trimmed || trimmed.length < 2 || !accountId || !holderId) {
      setResults([]);
      setShowDropdown(false);
      return;
    }

    if (debounceRef.current) clearTimeout(debounceRef.current);

    debounceRef.current = setTimeout(async () => {
      setIsSearching(true);
      setSearchError(null);
      try {
        const { users } = await socialMediaApi.searchTwitterUsers(
          holderId,
          trimmed,
          accountId,
          verifiedOnly,
        );
        setResults(users.filter((u) => !selectedUsernames.includes(u.username)));
        setShowDropdown(true);
      } catch {
        setSearchError('Search unavailable');
        setShowDropdown(false);
      } finally {
        setIsSearching(false);
      }
    }, 350);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query, accountId, holderId, selectedUsernames, verifiedOnly]);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const addUsername = (username: string) => {
    const clean = username.replace(/^@/, '').trim();
    if (!clean || selectedUsernames.includes(clean)) return;
    onChange([...selectedUsernames, clean]);
    setQuery('');
    setResults([]);
    setShowDropdown(false);
  };

  const removeUsername = (username: string) => {
    onChange(selectedUsernames.filter((u) => u !== username));
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if ((e.key === 'Enter' || e.key === ',') && query.trim()) {
      e.preventDefault();
      addUsername(query);
    }
    if (e.key === 'Backspace' && !query && selectedUsernames.length > 0) {
      removeUsername(selectedUsernames[selectedUsernames.length - 1]);
    }
    if (e.key === 'Escape') {
      setShowDropdown(false);
    }
  };

  return (
    <div ref={containerRef} className="space-y-2">
      {/* Input + verified filter toggle */}
      <div className="relative">
        <div
          className={cn(
            'flex items-center gap-1 focus-within:ring-1 focus-within:ring-ring',
            'w-full text-[12px] rounded-[10px] outline-none transition-all duration-150 shadow-social-card',
            'h-[40px] px-3 py-3 !font-mono',
            'text-primary',
            'bg-background  placeholder-[#171717]',
          )}
        >
          {isSearching ? (
            <Loader2 className="h-3 w-3 shrink-0 text-muted-foreground animate-spin" />
          ) : (
            <Search className="h-3 w-3 shrink-0 text-muted-foreground" />
          )}
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            onFocus={() => results.length > 0 && setShowDropdown(true)}
            disabled={disabled || !accountId}
            placeholder={accountId ? '@username or search…' : 'Connect a Twitter account first'}
            className="flex-1 bg-transparent text-xs focus:outline-none disabled:opacity-50 min-w-0"
          />
          {/* Verified-only toggle */}
          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => setVerifiedOnly((v) => !v)}
            disabled={disabled || !accountId}
            title={
              verifiedOnly ? 'Showing verified only — click to show all' : 'Show verified only'
            }
            className={cn(
              'shrink-0 flex items-center gap-0.5 px-1.5 h-5 rounded text-[10px] font-medium border transition-colors',
              verifiedOnly
                ? 'bg-sky-500/10 border-sky-500/40 text-sky-500'
                : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border',
            )}
          >
            <svg viewBox="0 0 24 24" className="h-3 w-3" fill="currentColor">
              <path d={VERIFIED_ICON_PATH} />
            </svg>
            {verifiedOnly && <span>Verified</span>}
          </button>
        </div>

        {/* Search results dropdown */}
        {showDropdown && results.length > 0 && (
          <div className="absolute top-full left-0 right-0 z-50 mt-1 rounded-md border border-border bg-popover shadow-md overflow-y-auto max-h-52">
            {results.map((user) => (
              <button
                key={user.id}
                type="button"
                onMouseDown={(e) => {
                  e.preventDefault();
                  addUsername(user.username);
                }}
                className="w-full flex items-start gap-2 px-2 py-2 text-left hover:bg-accent transition-colors"
              >
                {user.profileImageUrl ? (
                  <img
                    src={user.profileImageUrl}
                    alt=""
                    className="h-8 w-8 rounded-full flex-shrink-0 object-cover mt-0.5"
                  />
                ) : (
                  <div className="h-8 w-8 rounded-full bg-muted flex-shrink-0 mt-0.5" />
                )}
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1">
                    <p className="text-xs font-medium text-foreground truncate">{user.name}</p>
                    <VerifiedBadge type={user.verifiedType} />
                  </div>
                  <div className="flex items-center gap-1.5">
                    <p className="text-[10px] text-muted-foreground">@{user.username}</p>
                    {user.followersCount > 0 && (
                      <>
                        <span className="text-[10px] text-muted-foreground/50">·</span>
                        <p className="text-[10px] text-muted-foreground">
                          {formatFollowers(user.followersCount)} followers
                        </p>
                      </>
                    )}
                  </div>
                  {user.description && (
                    <p className="text-[10px] text-muted-foreground/70 truncate mt-0.5">
                      {user.description}
                    </p>
                  )}
                </div>
              </button>
            ))}
          </div>
        )}

        {/* Empty state when API returns no results for the current filter */}
        {showDropdown &&
          results.length === 0 &&
          query.replace(/^@/, '').trim().length >= 2 &&
          !isSearching && (
            <div className="absolute top-full left-0 right-0 z-50 mt-1 rounded-md border border-border bg-popover shadow-md px-3 py-2">
              <p className="text-[10px] text-muted-foreground">
                {verifiedOnly ? 'No verified users found.' : 'No users found.'}
              </p>
            </div>
          )}
      </div>

      {searchError && <p className="text-[10px] text-destructive">{searchError}</p>}

      {/* Selected usernames as chips */}
      {selectedUsernames.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {selectedUsernames.map((username) => (
            <span
              key={username}
              className={cn(
                'inline-flex items-center gap-1 rounded-full px-2 py-0.5',
                'bg-muted text-xs text-foreground border border-border',
              )}
            >
              @{username}
              {!disabled && (
                <button
                  type="button"
                  onClick={() => removeUsername(username)}
                  aria-label={`Remove @${username}`}
                  className="text-muted-foreground hover:text-foreground transition-colors"
                >
                  <X className="h-2.5 w-2.5" />
                </button>
              )}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
